import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { RouteErrorBoundary } from './app/RouteErrorBoundary';
import { createAppRouterContext } from './query/client';
import { routeTree } from './routeTree.gen';
import { deLocalizeUrl, localizeUrl } from './paraglide/runtime';
import { loadCatalogTextFor } from './i18n/catalogText';
import { syncDocumentLocale } from './i18n/appLocale';

// The router factory TanStack Start calls on both server and client. Keep this
// thin: route definitions live in src/routes/*. `defaultPreload: 'intent'`
// prefetches on hover/focus the way the old React Router lazy chunks did.
export function getRouter() {
  const context = createAppRouterContext();
  const router = createTanStackRouter({
    routeTree,
    context,
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: RouteErrorBoundary,
    // A translated page is the English route under a locale folder: `/zh` is
    // `/` rendered in Chinese. The router only ever sees the English path, and
    // every href it writes is put back under the current locale's folder when
    // that page is translated (scripts/i18nConfig.ts decides which are).
    rewrite: {
      input: ({ url }) => deLocalizeUrl(url),
      output: ({ url }) => localizeUrl(url),
    },
    // Awaited before the first client render, so hydration reads catalog text
    // in the language the server rendered it in (i18n/catalogText.ts).
    hydrate: (): Promise<void> => loadCatalogTextFor(router.matchRoutes(router.state.location)),
  });
  setupRouterSsrQueryIntegration({ router, queryClient: context.queryClient });
  // After each client render, including the first: records the language app
  // pages render in and keeps `<html lang>` on it (i18n/appLocale.ts).
  if (typeof document !== 'undefined') router.subscribe('onRendered', syncDocumentLocale);
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
  // Per-route layout hints read by the `_site` layout.
  interface StaticDataRouteOption {
    footer?: 'full' | 'slim';
    // The landing route, whose hero frame carries its own nav row: the sticky
    // bar stays veiled until the frame scrolls away (read by the `_site` layout).
    landing?: boolean;
    // Dashboard routes can opt out of the centered 1080px column and stretch
    // edge to edge (read by DashboardLayout).
    fullWidth?: boolean;
    // Full-bleed dashboard routes (e.g. the Agent) additionally drop the
    // content padding and fill the full height under the topbar.
    fullBleed?: boolean;
    // The page shows catalog data text, so its first client render waits for
    // the locale's translation memory (i18n/catalogText.ts).
    catalogText?: boolean;
  }
}
